import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { MousePointerClick, Play, Search, Sparkles } from 'lucide-react';
import { PageLayout } from '@/components/shared/PageLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getHelpVideoPublicUrl } from '@/config/environment-config';
import { useSettings } from '@/components/settings/lib/settings-context';
import { HelpPlayerPanel } from './HelpPlayerPanel';
import { HelpVideoList } from './HelpVideoList';
import { formatDuration, matchesSearch, publishedFirst, suggestNext, tasksThenClips, type Suggestion } from './help-gallery';
import { HELP_SECTION_ICONS } from './help-section-icons';
import { HELP_SECTIONS, isFeatureClip, type HelpEntry } from './help-types';
import { useHelpEntries } from './use-help-entries';
import { useHelpPlayer } from './use-help-player';
import { useWatchedHelp } from './use-watched-help';

/**
 * The Help page (`/help`, `/help/:subsection`): a compact gallery with the player on top.
 * The stage shows the playing video beside what it does and how to do it; until one is
 * chosen it suggests the video to watch first ("Start here", then "Next for you"). Below it,
 * one toolbar: search, and a section dropdown (`/help/<section>` opens on that section). The
 * chosen section is a list of accordion rows: task videos and Flows, then its feature clips.
 * Search lists matching rows across all sections. Sections the viewer cannot see (Admin, Remote Assessment without
 * Experimental Features) are left out.
 */
export default function HelpPage() {
  const { subsection } = useParams<{ subsection?: string }>();
  const { hash } = useLocation();
  const entries = useHelpEntries();
  // Gated sections (Remote Assessment) appear once the viewer's settings have loaded.
  const { isLoading: settingsLoading } = useSettings();
  const { watched, markWatched } = useWatchedHelp();
  const player = useHelpPlayer(entries);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<HTMLElement>(null);

  const sections = useMemo(
    () =>
      HELP_SECTIONS.map((section) => {
        const all = tasksThenClips(entries.filter((entry) => entry.section === section.id));
        const tasks = publishedFirst(all.filter((entry) => !isFeatureClip(entry)));
        const clips = publishedFirst(all.filter(isFeatureClip));
        return { ...section, tasks, clips, entries: [...tasks, ...clips] };
      }).filter((section) => section.entries.length > 0),
    [entries],
  );
  const [tab, setTab] = useState<string | null>(subsection ?? null);
  // A link to another section (`/help/sop`) switches the section. Choosing one from the
  // dropdown does not change the URL: /help and /help/:subsection are separate routes, and
  // remounting would stop the video.
  useEffect(() => {
    if (subsection) setTab(subsection);
  }, [subsection]);
  const current = sections.find((section) => section.id === tab) ?? sections[0];
  const activeTab = current?.id ?? '';

  const searching = query.trim() !== '';
  const results = sections
    .map((section) => ({ ...section, entries: section.entries.filter((entry) => matchesSearch(entry, query)) }))
    .filter((section) => section.entries.length > 0);
  const resultCount = results.reduce((sum, section) => sum + section.entries.length, 0);
  const suggestion = suggestNext(sections.flatMap((section) => section.entries), watched);

  // "/" focuses the search, as on most sites with a search box.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      const typing = target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]');
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const { play } = player;
  const playEntry = (entry: HelpEntry) => {
    const section = sections.find((candidate) => candidate.id === entry.section);
    play(entry, section?.entries ?? [entry]);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    stageRef.current?.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  // Deep links such as /help/sop#sop-goniometer open that video in the stage, once per hash,
  // so closing the player does not reopen it when settings finish loading.
  const openedHash = useRef<string | null>(null);
  useEffect(() => {
    if (!hash || openedHash.current === hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const section = sections.find((candidate) => candidate.entries.some((entry) => entry.id === id));
    const entry = section?.entries.find((candidate) => candidate.id === id);
    if (!section || !entry) return;
    openedHash.current = hash;
    setTab(section.id);
    play(entry, section.entries);
    stageRef.current?.scrollIntoView({ block: 'start' });
  }, [hash, sections, play]);

  // An unknown or hidden section goes back to /help, but only once settings are known,
  // so a bookmark to a gated section is not redirected before its flag has loaded.
  if (subsection && !sections.some((section) => section.id === subsection)) {
    return settingsLoading ? null : <Navigate to="/help" replace />;
  }

  return (
    <PageLayout title="Help" subtitle="Short videos that show how to do each task in Harry.">
      <section
        ref={stageRef}
        aria-label="Help player"
        className="relative mb-6 scroll-mt-24 rounded-2xl border border-border bg-card p-3 shadow-sm sm:p-4"
      >
        {player.current ? (
          <HelpPlayerPanel
            entry={player.current}
            queue={player.queue}
            watched={watched}
            onWatched={markWatched}
            onAdvance={player.advance}
            onPlayQueued={player.playQueued}
            onClose={player.close}
            layout="stage"
          />
        ) : suggestion ? (
          <SuggestionStage suggestion={suggestion} onPlay={playEntry} />
        ) : (
          <p className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground lg:py-24">
            <MousePointerClick className="h-4 w-4" aria-hidden />
            Choose a video below and it plays here.
          </p>
        )}
      </section>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search all help videos"
            aria-label="Search help"
            aria-keyshortcuts="/"
            className="h-11 pl-9 pr-10"
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 text-xs text-muted-foreground sm:block">
            /
          </kbd>
        </div>
        {!searching && (
          <Select value={activeTab} onValueChange={setTab}>
            <SelectTrigger aria-label="Help section" className="h-11 sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sections.map((section) => {
                const Icon = HELP_SECTION_ICONS[section.id];
                return (
                  <SelectItem key={section.id} value={section.id}>
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                      {section.label}
                      <span className="text-xs tabular-nums text-muted-foreground">{section.entries.length}</span>
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        <p className="sr-only" role="status" aria-live="polite">
          {searching ? `${resultCount} ${resultCount === 1 ? 'video' : 'videos'} found` : ''}
        </p>
      </div>

      {searching ? (
        results.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">No help matches “{query}”.</p>
            <Button variant="link" onClick={() => setQuery('')}>
              Clear search
            </Button>
          </div>
        ) : (
          <div className="space-y-5 pb-10">
            {results.map((section) => (
              <section key={section.id} aria-labelledby={`help-results-${section.id}`} className="space-y-2">
                <h2 id={`help-results-${section.id}`} className="text-sm font-semibold text-foreground">
                  {section.label}
                </h2>
                <HelpVideoList
                  entries={section.entries}
                  watched={watched}
                  currentId={player.current?.id ?? null}
                  onPlay={playEntry}
                  label={`${section.label} results`}
                />
              </section>
            ))}
          </div>
        )
      ) : (
        current && (
          <section aria-labelledby={`help-section-${current.id}`} className="space-y-5 pb-10">
            <div className="flex items-baseline justify-between gap-3 border-b border-border pb-2">
              <h2 id={`help-section-${current.id}`} className="text-lg font-semibold text-foreground">
                {current.label}
              </h2>
              <span className="text-xs text-muted-foreground">
                {current.entries.filter((entry) => watched.has(entry.id)).length} of {current.entries.length} watched
              </span>
            </div>
            {current.tasks.length > 0 && (
              <HelpVideoList
                entries={current.tasks}
                watched={watched}
                currentId={player.current?.id ?? null}
                onPlay={playEntry}
                label={`${current.label} videos`}
              />
            )}
            {current.clips.length > 0 && (
              <section aria-labelledby={`help-section-${current.id}-clips`} className="space-y-2">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <h3 id={`help-section-${current.id}-clips`} className="text-sm font-semibold text-foreground">
                    {current.label} clips
                  </h3>
                  <span className="text-xs text-muted-foreground">Short videos, one function each</span>
                </div>
                <HelpVideoList
                  entries={current.clips}
                  watched={watched}
                  currentId={player.current?.id ?? null}
                  onPlay={playEntry}
                  label={`${current.label} clips`}
                />
              </section>
            )}
          </section>
        )
      )}
    </PageLayout>
  );
}

/** Before a video is chosen: the one to watch first, a large poster beside why it is suggested. */
function SuggestionStage({ suggestion, onPlay }: { suggestion: Suggestion; onPlay: (entry: HelpEntry) => void }) {
  const { entry, reason } = suggestion;
  const poster = entry.poster ? getHelpVideoPublicUrl(entry.poster) : null;
  return (
    <section aria-labelledby="help-suggestion-title" className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <button
        type="button"
        onClick={() => onPlay(entry)}
        className="group relative block aspect-video w-full overflow-hidden rounded-xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Play ${entry.title}`}
      >
        {poster && <img src={poster} alt="" className="h-full w-full object-cover" />}
        <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/20">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform group-hover:scale-110 motion-reduce:transition-none">
            <Play className="ml-1 h-7 w-7 fill-current" aria-hidden />
          </span>
        </span>
        {entry.durationSec !== null && (
          <span className="absolute bottom-2 right-2 rounded bg-black/75 px-2 py-0.5 text-sm font-medium tabular-nums text-white">
            {formatDuration(entry.durationSec)}
          </span>
        )}
      </button>
      <div className="flex flex-col justify-center gap-3 p-1 sm:p-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {reason === 'start' ? 'New to Harry? Start here' : 'Next for you'}
        </p>
        <h2 id="help-suggestion-title" className="text-xl font-semibold leading-tight text-foreground sm:text-2xl">
          {entry.title}
        </h2>
        {entry.purpose && <p className="text-sm text-muted-foreground">{entry.purpose}</p>}
        <div>
          <Button size="lg" onClick={() => onPlay(entry)} className="gap-2">
            <Play className="h-4 w-4 fill-current" aria-hidden />
            Watch{entry.durationSec !== null ? ` (${formatDuration(entry.durationSec)})` : ''}
          </Button>
        </div>
      </div>
    </section>
  );
}
